/**
 * feature-flags-store — FEAT-V2-019 UI (admin /admin/feature-flags).
 *
 * Hooks TanStack Query pra consumir o backend admin-feature-flags.ts:
 *   - useFeatureFlags() — lista todas as flags com source/override info
 *   - useFeatureFlagActions() — toggle (PUT) + reset (DELETE override)
 *
 * Backend shape (server/routes/admin-feature-flags.ts FlagDto):
 *   { name, enabled, source: 'db'|'env'|'default', envValue, override: {...}|null }
 *
 * Estratégia de cache:
 *   - staleTime 30s (flags mudam raramente; admin que acabou de toggle
 *     vê resultado via invalidate explícita)
 *   - invalidate após PUT/DELETE — backend tem cache TTL 5s no isFlagEnabled,
 *     então UI mostra valor novo imediatamente mas requests de outros endpoints
 *     podem demorar até 5s pra refletir (documentado).
 */

import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';

export type FeatureFlagSource = 'db' | 'env' | 'default';

export interface FeatureFlagOverrideInfo {
  changedByName: string | null;
  changedAtIso: string;
  notes: string | null;
}

export interface FeatureFlagDto {
  name: string;
  enabled: boolean;
  source: FeatureFlagSource;
  envValue: string | null;
  override: FeatureFlagOverrideInfo | null;
}

export const FEATURE_FLAGS_KEYS = {
  all: ['v1', 'admin', 'feature-flags'] as const,
  list: () => ['v1', 'admin', 'feature-flags', 'list'] as const,
};

/**
 * GET /api/v1/admin/feature-flags — lista canônica de todas as flags.
 * Backend retorna ordem fixa (FLAG_NAMES em server/lib/feature-flags.ts).
 *
 * `enabled: false` quando user sem token (evita request 401 ruidoso) —
 * UI consome sob /admin que já tem requireAuth no AdminLayout.
 */
export function useFeatureFlags() {
  return useQuery({
    queryKey: FEATURE_FLAGS_KEYS.list(),
    queryFn: () => api.get<FeatureFlagDto[]>('/api/v1/admin/feature-flags'),
    enabled: hasToken(),
    staleTime: 30_000,
  });
}

export interface UpdateFlagInput {
  enabled: boolean;
  notes?: string | null;
}

/**
 * Hook de mutations — PUT (set override) + DELETE (reset pro default).
 *
 * `pending` é o nome da flag em curso (ou null). Permite UI mostrar spinner
 * por linha sem bloquear a tabela inteira.
 *
 * Após PUT/DELETE: invalida a query da lista pra refletir source='db' /
 * 'default' atualizado.
 */
export function useFeatureFlagActions() {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: FEATURE_FLAGS_KEYS.all });

  const update = useCallback(
    async (
      name: string,
      input: UpdateFlagInput,
    ): Promise<{ ok: true; flagName: string; enabled: boolean }> => {
      setPending(name);
      try {
        const r = await api.put<{ ok: true; flagName: string; enabled: boolean }>(
          `/api/v1/admin/feature-flags/${encodeURIComponent(name)}`,
          input,
        );
        await invalidate();
        return r;
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc],
  );

  const reset = useCallback(
    async (name: string): Promise<void> => {
      setPending(name);
      try {
        await api.delete(`/api/v1/admin/feature-flags/${encodeURIComponent(name)}`);
        await invalidate();
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc],
  );

  return { update, reset, pending };
}

// ─── Frontend runtime flags (Projetos 2.0) ──────────────────────────────
//
// Leitura por QUALQUER colaborador logado (não-admin) via GET
// /api/v1/feature-flags/me — devolve só a allowlist `FRONTEND_FLAGS` do
// backend. Usado pra gating de UI (ex.: wizard de Novo Projeto decide entre o
// Step 1 atual e a origem real conforme PROJETOS_V2_ENABLED).
//
// Diferente de useFeatureFlags() (admin-only, tabela completa com origem/notas).

export type FrontendFlagName =
  | 'PROJETOS_V2_ENABLED'
  | 'COMPRAS_WORKFLOW_ENABLED'
  | 'FINANCEIRO_WORKFLOW_ENABLED'
  | 'PORTAL_CLIENTE_PROJETOS_ENABLED'
  | 'TASKS_ENABLED'
  | 'ADMIN_IMPERSONATE_ENABLED'
  | 'ORCAMENTO_ENABLED'
  // Round D — módulo de Compras (Catálogos → Compras).
  | 'COMPRAS_ENABLED'
  // COMERCIAL-REBUILD — CRM v2 (Comercial novo: Leads/Funil/Indicadores/Metas).
  | 'CRM_V2_ENABLED'
  // MONITORAMENTO fase 1 (mig 515) — Manutenção › Monitoramento.
  // ⚠️ Nasce DESLIGADA: quem liga é o Matheus, quando o coletor existir.
  | 'MONITORAMENTO_ENABLED'
  // RH-MENU (03/10/2026) — o menu RH e as telas /rh. ⚠️ Nasce DESLIGADA.
  | 'MENU_RH_ENABLED'
  // TESTES DISC e QA (04/10/2026) — aba "Perfil comportamental" e "Meus testes". ⚠️ Nasce DESLIGADA.
  | 'TESTES_COMPORTAMENTAIS_ENABLED'
  // FEEDBACK (C1, 05/10/2026) — aba "Feedback", "Painel de feedback" e "Meu desenvolvimento". ⚠️ Nasce DESLIGADA.
  | 'FEEDBACK_ENABLED';

export const MY_FEATURE_FLAGS_KEY = ['v1', 'feature-flags', 'me'] as const;

/** GET /api/v1/feature-flags/me — { flags: { [name]: boolean } }. */
export function useMyFeatureFlags() {
  return useQuery({
    queryKey: MY_FEATURE_FLAGS_KEY,
    queryFn: () => api.get<{ flags: Record<string, boolean> }>('/api/v1/feature-flags/me'),
    enabled: hasToken(),
    staleTime: 30_000,
  });
}

/**
 * Conveniência: retorna se uma flag de frontend está ON. Default `false`
 * enquanto carrega ou se ausente — UI fica no caminho atual até confirmar ON
 * (fail-safe: nunca mostra feature nova antes de saber que está ligada).
 */
export function useMyFeatureFlag(name: FrontendFlagName): boolean {
  const { data } = useMyFeatureFlags();
  return data?.flags?.[name] ?? false;
}
